// Furnace window (UI-3; M6.2). Input + fuel + a take-only output over a furnace
// block entity, plus the player inventory. Cooking runs in the tick loop (phase
// 6); the window polls a repaint so the output appears live, and taking the
// output banks the furnace's XP to the player (PLR-5). Client-only (ENG-1).
import { collectFurnaceXp, furnaceSlots, FURNACE_INPUT, FURNACE_FUEL, FURNACE_OUTPUT, type FurnaceData } from '../../../sim/game/containers/furnace';
import { maxStackFn, playerRefs, playerRows } from './crafting';
import { openWindow, refRange, returnToInventory, type SlotRef, type WindowConfig } from './container';
import type { Game } from '../../boot';

/** Open a furnace block entity's window. */
export function openFurnace(game: Game, furnace: FurnaceData): () => void {
  const slots = furnaceSlots(furnace, game.registries, maxStackFn(game.registries));
  const [input, fuel, output] = refRange(slots, 0, 3);
  input!.testId = 'furnace-input';
  fuel!.testId = 'furnace-fuel';
  output!.role = 'output';
  output!.testId = 'furnace-output';
  const pr = playerRefs(game.playerState.inventory);
  const furnaceSet = new Set<object>([slots]);

  // input over fuel on the left; output on the right (with gaps for the arrow).
  const layout: (SlotRef | null)[][] = [
    [input!, null, output!],
    [fuel!],
    [null],
    ...playerRows(pr),
  ];

  let stop: (() => void) | null = null;
  const cfg: WindowConfig = {
    title: 'Furnace',
    layout,
    quickTargets: (ref) => {
      if (furnaceSet.has(ref.slots)) return [...pr.storage, ...pr.hotbar];
      // Player → furnace: fuels head to the fuel slot, everything else to input.
      const stack = ref.slots.get(ref.index);
      return slots.accepts(FURNACE_FUEL, stack) ? [fuel!, input!] : [input!];
    },
    onOutputTaken: () => {
      const xp = collectFurnaceXp(furnace);
      if (xp > 0) game.playerState.addExperience(xp);
    },
    onOpen: (repaint) => {
      const timer = window.setInterval(repaint, 120); // keep the cook gauge live
      stop = () => window.clearInterval(timer);
    },
    onClose: (cursor) => { stop?.(); returnToInventory(game, [cursor]); },
  };
  void FURNACE_INPUT; void FURNACE_OUTPUT;
  return openWindow(game, cfg);
}
